
/**
 * 智能文案系统 API 服务
 */

import api from '../../../../services/api';
import { fetchSSE } from './utils/streamUtils';
import type {
    SmartCopywritingProject,
    SmartCopywritingProjectBrief,
    CreateProjectRequest,
    AnalyzeAudienceRequest,
    RecommendScenariosRequest,
    GenerateCopyRequest,
    GenerateCopyResult,
    RegenerateSellingPointRequest,
    RegenerateSellingPointResult,
    SaveVersionRequest,
    CopywritingVersion,
    CopywritingVersionBrief,
    TargetAudience,
    UsageScenario,
    AudienceTemplate,
    ScenarioTemplate,
} from './types';

const BASE_URL = '/smart-copywriting';

export const smartCopywritingAPI = {
    // ========== 项目 CRUD ==========

    /** 获取项目列表 */
    async getProjects(): Promise<SmartCopywritingProjectBrief[]> {
        const response = await api.get(`${BASE_URL}/`);
        return response.data;
    },

    /** 获取项目详情 */
    async getProject(id: number): Promise<SmartCopywritingProject> {
        const response = await api.get(`${BASE_URL}/${id}/`);
        return response.data;
    },

    /** 根据SKU查找项目（返回最新的一个） */
    async getProjectBySku(skuId: number): Promise<SmartCopywritingProject | null> {
        try {
            const response = await api.get(`${BASE_URL}/`, { params: { sku: skuId } });
            const projects = response.data.results || response.data || [];
            return projects.length > 0 ? projects[0] : null;
        } catch {
            return null;
        }
    },

    /** 创建项目 */
    async createProject(data: CreateProjectRequest): Promise<SmartCopywritingProject> {
        const response = await api.post(`${BASE_URL}/`, data);
        return response.data;
    },

    /** 更新项目 */
    async updateProject(id: number, data: Partial<SmartCopywritingProject>): Promise<SmartCopywritingProject> {
        const response = await api.patch(`${BASE_URL}/${id}/`, data);
        return response.data;
    },

    /** 删除项目 */
    async deleteProject(id: number): Promise<void> {
        await api.delete(`${BASE_URL}/${id}/`);
    },

    // ========== AI 分析 ==========

    /** 分析目标人群（批量模式，超时3分钟） */
    async analyzeAudience(projectId: number, data: AnalyzeAudienceRequest): Promise<{
        audiences: TargetAudience[];
        message: string;
    }> {
        const response = await api.post(`${BASE_URL}/${projectId}/analyze-audience/`, data, {
            timeout: 180000  // 3分钟超时
        });
        return response.data;
    },

    /** 流式分析目标人群（SSE模式，实时显示每个人群） */
    analyzeAudienceStream(
        projectId: number,
        data: AnalyzeAudienceRequest,
        callbacks: {
            onAudience: (audience: TargetAudience) => void;
            onComplete: (count: number) => void;
            onError: (error: string) => void;
        }
    ): () => void {
        const url = `/api${BASE_URL}/${projectId}/analyze-audience/?stream=true`;
        return fetchSSE<TargetAudience>(url, data, {
            onEvent: (type, data) => {
                if (type === 'audience') {
                    callbacks.onAudience(data);
                }
            },
            onComplete: callbacks.onComplete,
            onError: callbacks.onError,
        });
    },

    /** 流式推荐使用场景（SSE模式，实时显示每个场景） */
    recommendScenariosStream(
        projectId: number,
        data: RecommendScenariosRequest,
        callbacks: {
            onScenario: (scenario: UsageScenario) => void;
            onComplete: (count: number) => void;
            onError: (error: string) => void;
        }
    ): () => void {
        const url = `/api${BASE_URL}/${projectId}/recommend-scenarios/?stream=true`;
        return fetchSSE<UsageScenario>(url, data, {
            onEvent: (type, data) => {
                if (type === 'scenario') {
                    callbacks.onScenario(data);
                }
            },
            onComplete: callbacks.onComplete,
            onError: callbacks.onError,
        });
    },

    /** 推荐使用场景（常规模式） */
    async recommendScenarios(projectId: number, data: RecommendScenariosRequest): Promise<{
        scenarios: UsageScenario[];
        message: string;
    }> {
        const response = await api.post(`${BASE_URL}/${projectId}/recommend-scenarios/`, data, {
            timeout: 120000  // 2分钟超时
        });
        return response.data;
    },

    /** 生成文案（超时3分钟） */
    async generateCopy(projectId: number, data: GenerateCopyRequest): Promise<{
        result: GenerateCopyResult;
        message: string;
    }> {
        const response = await api.post(`${BASE_URL}/${projectId}/generate-copy/`, data, {
            timeout: 180000  // 3分钟超时
        });
        return response.data;
    },

    /** 重写单个卖点 */
    async regenerateSellingPoint(projectId: number, data: RegenerateSellingPointRequest): Promise<RegenerateSellingPointResult> {
        const response = await api.post(`${BASE_URL}/${projectId}/regenerate-selling-point/`, data, {
            timeout: 60000 // 1分钟超时
        });
        return response.data;
    },

    // ========== 版本管理 ==========

    /** 保存版本 */
    async saveVersion(projectId: number, data: SaveVersionRequest): Promise<{
        version: CopywritingVersion;
        message: string;
    }> {
        const response = await api.post(`${BASE_URL}/${projectId}/save-version/`, data);
        return response.data;
    },

    /** 获取版本列表 */
    async getVersions(projectId: number): Promise<CopywritingVersionBrief[]> {
        const response = await api.get(`${BASE_URL}/${projectId}/versions/`);
        return response.data;
    },

    /** 获取版本详情 */
    async getVersion(projectId: number, versionId: number): Promise<CopywritingVersion> {
        const response = await api.get(`${BASE_URL}/${projectId}/versions/${versionId}/`);
        return response.data;
    },
};

// ========== 模板 API ==========

export const templateAPI = {
    /** 获取人群模板（按类别分组） */
    async getAudienceTemplatesByCategory(): Promise<Record<string, AudienceTemplate[]>> {
        const response = await api.get('/audience-templates/by-category/');
        return response.data;
    },

    /** 获取所有人群模板 */
    async getAudienceTemplates(): Promise<AudienceTemplate[]> {
        const response = await api.get('/audience-templates/');
        return response.data;
    },

    /** 获取场景模板 */
    async getScenarioTemplates(): Promise<ScenarioTemplate[]> {
        const response = await api.get('/scenario-templates/');
        return response.data;
    },

    /** 根据人群筛选场景 */
    async filterScenariosByAudience(audiences: string[]): Promise<ScenarioTemplate[]> {
        const response = await api.post('/scenario-templates/filter-by-audience/', { audiences });
        return response.data;
    },
};

export default smartCopywritingAPI;
